import {FC} from 'react'
import {Controller, SubmitHandler, useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import {StressCheck, Healthcare} from 'jbc-front/components/icons'
import Button from 'jbc-front/components/Button'
import {Modal} from 'jbc-front/components/presenters/ui/Modal'
import {Checkbox} from 'jbc-front/components/presenters/form/Checkbox'
import {Label} from 'jbc-front/components/presenters/form/Label'
import {Select} from 'jbc-front/components/presenters/form/Select'
import {ErrorMessage} from 'jbc-front/components/presenters/form/ErrorMessage'
import {Schema, schema} from './schema'
import styles from './HealthcareStartModal.scss'

const stressCheckRollTypes = [
  {label: '実施者（全従業員の個人結果を確認できる方、産業医等）', value: 'practitioner'},
  {
    label: '実務担当者（労務担当者等）',
    value: 'person_in_charge',
  },
]

interface ModalBodyProps {
  onSubmit: SubmitHandler<Schema>
}

export const ModalBody: FC<ModalBodyProps> = ({onSubmit}) => {
  const {
    handleSubmit,
    register,
    watch,
    control,
    formState: {errors},
  } = useForm<Schema>({
    resolver: yupResolver(schema),
    defaultValues: {
      stressCheck: false,
      stressCheckRole: null,
      regularHealthCheckup: false,
    },
  })

  const stressCheck = watch('stressCheck')
  const regularHealthCheckup = watch('regularHealthCheckup')

  return (
    <Modal.Body className={styles.body}>
      <div className={styles.description}>
        健康管理機能の利用を開始します。
        <br />
        利用する機能にチェックを入れて「利用開始する」ボタンを押してください。
      </div>
      <ul className={styles.notice}>
        <li>・追加料金は発生いたしません</li>
        <li>・二人目以降の管理者は健康管理画面内の権限管理画面から追加できます</li>
      </ul>
      <div className={styles.feature}>
        <h4 className={styles.featureTitle}>
          <StressCheck className={styles.featureIcon} size={20} />
          ストレスチェック
        </h4>
        <Checkbox {...register('stressCheck')}>利用する</Checkbox>
        <div className={styles.stressCheckRole}>
          <Label>あなたの役割</Label>
          <Controller
            control={control}
            name="stressCheckRole"
            render={({field: {onChange, onBlur, value}}) => (
              <Select
                className={styles.select}
                options={stressCheckRollTypes}
                onChange={({value}) => onChange(value)}
                onBlur={onBlur}
                value={stressCheckRollTypes.find((option) => option.value === value)}
                disabled={!stressCheck}
                placeholder="選択してください"
              />
            )}
          />
          {errors.stressCheckRole && <ErrorMessage>{errors.stressCheckRole.message}</ErrorMessage>}
          <ul className={styles.stressCheckNotice}>
            <li>・「利用する」にチェックを入れると役割を選択できます。</li>
            <li>
              ・ストレスチェックの結果等の閲覧権限がある方のみ、自身の役割を選択してストレスチェック機能の利用を開始してください。
            </li>
            <li>・ストレスチェックは法律上閲覧できる管理者が制限されています。</li>
          </ul>
        </div>
      </div>
      <div className={styles.feature}>
        <h4 className={styles.featureTitle}>
          <Healthcare className={styles.featureIcon} size={20} />
          定期健康診断
        </h4>
        <Checkbox {...register('regularHealthCheckup')}>利用する</Checkbox>
      </div>
      <div className={styles.buttons}>
        <Button primary widthWide onClick={handleSubmit(onSubmit)} disabled={!stressCheck && !regularHealthCheckup}>
          利用開始する
        </Button>
      </div>
    </Modal.Body>
  )
}
